/**
 * Chart modal content - date range controls and chart area.
 * Extracted for reuse; wrapped by MarketTrendsModal in a Dialog.
 */
import React from 'react';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Chart } from 'react-chartjs-2';
import type { ChartOptions } from 'chart.js';

export interface ChartModalProps {
  selectedCompany: string | null;
  tempDateRange: { startDate: Date | null; endDate: Date | null };
  calendarViewDates: { startViewDate: Date | null; endViewDate: Date | null };
  getDefaultViewDate: (isStart?: boolean) => Date;
  handleTempDateRangeChange: (start: Date | null, end: Date | null) => void;
  setCalendarViewDates: (fn: ((prev: any) => any) | any) => void;
  handleApplyDateFilter: () => void;
  isLoadingMarketTrends: boolean;
  filteredMarketSalesTrendData: any[];
  marketSalesTrendData: any[];
  marketTrendsData: any;
  marketTrendsOptions: ChartOptions;
  classes: any;
}

export function ChartModal({
  selectedCompany,
  tempDateRange,
  calendarViewDates,
  getDefaultViewDate,
  handleTempDateRangeChange,
  setCalendarViewDates,
  handleApplyDateFilter,
  isLoadingMarketTrends,
  filteredMarketSalesTrendData,
  marketSalesTrendData,
  marketTrendsData,
  marketTrendsOptions,
  classes,
}: ChartModalProps) {
  return (
    <div style={{ padding: '1rem' }}>
      <div className={classes.chartControls} style={{ marginBottom: '1rem' }}>
        <div className={classes.dateRangePicker}>
          <label style={{ fontSize: '0.75rem', marginRight: '0.5rem' }}>Date Range:</label>
          <Calendar
            key={`modal-start-date-${selectedCompany ? selectedCompany : 'all'}`}
            value={tempDateRange.startDate}
            viewDate={
              calendarViewDates.startViewDate ||
              tempDateRange.startDate ||
              getDefaultViewDate(true)
            }
            onChange={e => {
              const selectedDate = e.value as Date;
              if (selectedDate) {
                handleTempDateRangeChange(selectedDate, tempDateRange.endDate);
                setCalendarViewDates((prev: any) => ({ ...prev, startViewDate: selectedDate }));
              } else {
                handleTempDateRangeChange(null, tempDateRange.endDate);
                setCalendarViewDates((prev: any) => ({ ...prev, startViewDate: null }));
              }
            }}
            onViewDateChange={e => {
              const viewDate = e.value as Date;
              if (viewDate) {
                handleTempDateRangeChange(viewDate, tempDateRange.endDate);
                setCalendarViewDates((prev: any) => ({ ...prev, startViewDate: viewDate }));
              }
            }}
            placeholder="Start Date"
            dateFormat="mm/dd/yy"
            yearNavigator
            monthNavigator
            yearRange="2010:2030"
            showIcon
            icon="pi pi-calendar"
            appendTo={typeof document !== 'undefined' ? document.body : undefined}
            style={{ width: '140px', fontSize: '0.75rem' }}
            showOnFocus={false}
            className="chart-date-picker"
          />
          <span style={{ margin: '0 0.5rem' }}>-</span>
          <Calendar
            key={`modal-end-date-${selectedCompany ? selectedCompany : 'all'}`}
            value={tempDateRange.endDate}
            viewDate={
              calendarViewDates.endViewDate ||
              tempDateRange.endDate ||
              getDefaultViewDate(false)
            }
            onChange={e => {
              const selectedDate = e.value as Date;
              if (selectedDate) {
                handleTempDateRangeChange(tempDateRange.startDate, selectedDate);
                setCalendarViewDates((prev: any) => ({ ...prev, endViewDate: selectedDate }));
              } else {
                handleTempDateRangeChange(tempDateRange.startDate, null);
                setCalendarViewDates((prev: any) => ({ ...prev, endViewDate: null }));
              }
            }}
            onViewDateChange={e => {
              const viewDate = e.value as Date;
              if (viewDate) {
                handleTempDateRangeChange(tempDateRange.startDate, viewDate);
                setCalendarViewDates((prev: any) => ({ ...prev, endViewDate: viewDate }));
              }
            }}
            placeholder="End Date"
            dateFormat="mm/dd/yy"
            yearNavigator
            monthNavigator
            yearRange="2010:2030"
            showIcon
            icon="pi pi-calendar"
            appendTo={typeof document !== 'undefined' ? document.body : undefined}
            style={{ width: '140px', fontSize: '0.75rem' }}
            showOnFocus={false}
            className="chart-date-picker"
          />
          <Button
            label="Apply Filter"
            icon="pi pi-filter"
            onClick={handleApplyDateFilter}
            disabled={isLoadingMarketTrends}
            style={{
              marginLeft: '0.5rem',
              padding: '0.625rem 0.75rem',
              fontSize: '0.75rem',
              height: '2.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              backgroundColor: '#ffffff',
              color: '#374151',
            }}
          />
        </div>
      </div>

      <div style={{ height: 'calc(90vh - 150px)', width: '100%', position: 'relative' }}>
        {isLoadingMarketTrends ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: '#522d6e',
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <i
                className="pi pi-spinner pi-spin"
                style={{ fontSize: '2rem', marginBottom: '0.5rem' }}
              />
              <p>
                Loading market sales trend data
                {selectedCompany ? ` for ${selectedCompany}` : ''}...
              </p>
            </div>
          </div>
        ) : filteredMarketSalesTrendData.length > 0 || marketSalesTrendData.length > 0 ? (
          <Chart type="bar" data={marketTrendsData as any} options={marketTrendsOptions} />
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: '#6b7280',
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <i
                className="pi pi-chart-line"
                style={{ fontSize: '2rem', marginBottom: '0.5rem' }}
              />
              <p>
                No market sales trend data available. Please validate NDC to load chart data.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
